import { ref } from 'vue'
import { suratKeluarAPI } from '@/services/axios/perki'

/**
 * Composable for Surat Keluar form validation
 * Handles validation rules and unique nomor surat checking
 */
export function useSuratKeluarValidation() {
  // State for nomor surat unique validation (Lainnya type)
  const nomorSuratError = ref('')
  const nomorSuratChecking = ref(false)

  /**
   * Validation rules for Surat Keluar form
   */
  const rules = {
    jenisSurat: [{ required: true, message: 'Jenis surat harus dipilih' }],
    nomorSurat: [{ required: true, message: 'Nomor surat harus diisi' }],
    sifatSurat: [{ required: true, message: 'Sifat surat harus dipilih' }],
    bentukSurat: [{ required: true, message: 'Bentuk surat harus dipilih' }],
    namaKegiatan: [{ required: true, message: 'Nama kegiatan harus diisi' }],
    waktuPerjalananMulai: [{ required: true, message: 'Waktu perjalanan mulai harus dipilih' }],
    waktuPerjalananSelesai: [
      { required: false, message: 'Waktu perjalanan selesai harus dipilih' },
    ],
    tempatKegiatan: [{ required: true, message: 'Tempat kegiatan harus diisi' }],
    tanggalSurat: [{ required: true, message: 'Tanggal surat harus dipilih' }],
    penandatanganKiriId: [{ required: true, message: 'Penandatangan 1 harus dipilih' }],
    keperluan: [{ required: true, message: 'Keperluan harus diisi' }],
    perihal: [{ required: true, message: 'Perihal / Judul surat harus diisi' }],
    body: [{ required: true, message: 'Isi surat harus diisi' }],
  }

  /**
   * Check if nomor surat is unique (for Lainnya type)
   * @param {string} nomorSuratValue - The nomor surat to check
   * @param {string|null} excludeId - Optional ID to exclude (for edit mode)
   * @returns {Promise<boolean>} - True if unique, false if duplicate
   */
  const checkNomorSuratUnique = async (nomorSuratValue, excludeId = null) => {
    if (!nomorSuratValue || nomorSuratValue.trim() === '') {
      nomorSuratError.value = ''
      return true
    }

    nomorSuratChecking.value = true
    nomorSuratError.value = ''

    try {
      const response = await suratKeluarAPI.checkNomorSurat(nomorSuratValue.trim(), excludeId)
      if (response.data?.data) {
        const { isUnique } = response.data.data
        if (!isUnique) {
          nomorSuratError.value =
            'Nomor surat sudah digunakan. Silakan gunakan nomor surat yang berbeda.'
          return false
        }
      }
      return true
    } catch (error) {
      console.error('Error checking nomor surat:', error)
      return true // Allow submission on error
    } finally {
      nomorSuratChecking.value = false
    }
  }

  /**
   * Clear nomor surat error state
   */
  const clearNomorSuratError = () => {
    nomorSuratError.value = ''
    nomorSuratChecking.value = false
  }

  /**
   * Validate Surat Tugas specific fields
   * @param {Object} form - The form data
   * @returns {{ valid: boolean, message?: string }}
   */
  const validateSuratTugas = form => {
    if (!form.namaKegiatan) {
      return { valid: false, message: 'Nama kegiatan harus diisi' }
    }
    if (!form.waktuPerjalananMulai) {
      return { valid: false, message: 'Waktu perjalanan mulai harus dipilih' }
    }
    if (form.waktuPerjalananSelesai && form.waktuPerjalananMulai) {
      const mulai = form.waktuPerjalananMulai.valueOf
        ? form.waktuPerjalananMulai.valueOf()
        : new Date(form.waktuPerjalananMulai).getTime()
      const selesai = form.waktuPerjalananSelesai.valueOf
        ? form.waktuPerjalananSelesai.valueOf()
        : new Date(form.waktuPerjalananSelesai).getTime()
      if (selesai < mulai) {
        return { valid: false, message: 'Waktu selesai tidak boleh sebelum waktu mulai' }
      }
    }
    if (!form.tempatKegiatan) {
      return { valid: false, message: 'Tempat kegiatan harus diisi' }
    }
    if (!form.keperluan) {
      return { valid: false, message: 'Keperluan harus diisi' }
    }
    if (!form.penandatanganKiriId) {
      return { valid: false, message: 'Penandatangan 1 harus dipilih' }
    }

    // Validate perwakilan dinas
    const validPerwakilan = form.perwakilanDinas?.filter(item => item.userId) || []
    if (validPerwakilan.length === 0) {
      return { valid: false, message: 'Minimal harus ada 1 perwakilan dinas' }
    }

    return { valid: true }
  }

  /**
   * Validate Surat Lainnya specific fields
   * @param {Object} form - The form data
   * @returns {{ valid: boolean, message?: string }}
   */
  const validateSuratLainnya = form => {
    if (!form.nomorSuratLainnya) {
      return { valid: false, message: 'Nomor surat harus diisi' }
    }
    if (nomorSuratError.value) {
      return { valid: false, message: 'Nomor surat sudah digunakan' }
    }
    if (!form.perihal) {
      return { valid: false, message: 'Perihal / Judul surat harus diisi' }
    }
    if (!form.body) {
      return { valid: false, message: 'Isi surat harus diisi' }
    }
    if (!form.penandatanganKiriId) {
      return { valid: false, message: 'Penandatangan 1 harus dipilih' }
    }

    return { valid: true }
  }

  /**
   * Validate common fields for both types
   * @param {Object} form - The form data
   * @returns {{ valid: boolean, message?: string }}
   */
  const validateCommonFields = form => {
    if (!form.tanggalSurat) {
      return { valid: false, message: 'Tanggal surat harus dipilih' }
    }
    if (!form.sifatSurat) {
      return { valid: false, message: 'Sifat surat harus dipilih' }
    }
    if (!form.bentukSurat) {
      return { valid: false, message: 'Bentuk surat harus dipilih' }
    }

    return { valid: true }
  }

  /**
   * Validate arsip surat form
   * @param {Object} surat - The surat data
   * @param {number} index - The surat index for error message
   * @returns {{ valid: boolean, message?: string }}
   */
  const validateArsipSurat = (surat, index) => {
    const prefix = `Surat ${index + 1}: `

    if (!surat.nomorSurat) {
      return { valid: false, message: `${prefix}Nomor surat harus diisi` }
    }
    if (!surat.tanggalSurat) {
      return { valid: false, message: `${prefix}Tanggal surat harus dipilih` }
    }
    if (!surat.penandatanganId) {
      return { valid: false, message: `${prefix}Penandatangan harus dipilih` }
    }

    return { valid: true }
  }

  return {
    // State
    nomorSuratError,
    nomorSuratChecking,

    // Rules
    rules,

    // Methods
    checkNomorSuratUnique,
    clearNomorSuratError,
    validateSuratTugas,
    validateSuratLainnya,
    validateCommonFields,
    validateArsipSurat,
  }
}
